import {
  BillingError,
  PaymentRejectedError,
  UnknownApiError,
} from './errors';
import {
  assertAuthData,
  assertString,
  assertBoolean,
  assertNumber,
  assertStringArray,
  assertRequiredString,
  assertRequiredStringArray,
} from './utils';

export default function (api) {
  /**
   * @typedef {Object} PaymentMethod
   * @property {string} method
   * @property {Object} data
   * @property {string} data.ccCardholderName
   * @property {number} data.ccExpMonth
   * @property {number} data.ccExpYear
   * @property {string} data.ccFinancialInstitution
   * @property {string} data.ccMaskedNumber
   */

  /**
   * @typedef {Object} AdyenPublicEncryptionKeyResponse
   * @property {string} publicEncryptionKey
   * @property {string} generationTime
   */

  /**
   * @typedef {Object} Payment
   * @property {string} userId
   * @property {string} transactionId
   * @property {string} status
   * @property {string} transactionType
   * @property {string} paymentMethod
   * @property {number} fromDate
   * @property {number} toDate
   * @property {string[]} productIds
   * @property {number} grossPrice
   * @property {number} netPrice
   * @property {string} currency
   * @property {number} timestamp
   * @property {string[]} productNames
   */

  /**
   * @memberof API
   * @instance
   */
  const billing = {
    /**
     * Get current user's payment method.
     * Returns null if user has no payment method.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise}
     * @fulfill {PaymentMethod}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError}
     */
    async getPaymentMethod() {
      assertAuthData(api.authData);

      const url = `/billing/v2/users/${api.authData.userId}/payment_method`;
      const res = await api.request('get', url);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 404: {
          return null;
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Gets the user's payments history.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise}
     * @fulfill {Array.<Payment>}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError}
     */
    async payments() {
      assertAuthData(api.authData);

      const url = `/billing/v2/users/${api.authData.userId}/payments`;
      const res = await api.request('get', url);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Gets the user's aggregate payments history.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise}
     * @fulfill {Array.<Payment>}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError}
     */
    async aggregatePayments() {
      assertAuthData(api.authData);

      const url = `/billing/v2/users/${api.authData.userId}/payments/aggregate`;
      const res = await api.request('get', url);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Makes an online payment with the active payment method.
     * Auth token is required.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string[]} payload.productIds
     * @param {boolean=} payload.personalisation
     * @param {string=} payload.promoCode
     * @param {number=} payload.netPrice - Net price in cents
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {BillingError}
     * @reject {UnknownApiError}
     */
    async online(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredStringArray(payload.productIds, 'payload.productIds');
      assertBoolean(payload.personalisation, 'payload.personalisation');
      assertString(payload.promoCode, 'payload.promoCode');
      assertNumber(payload.netPrice, 'payload.netPrice');

      const data = { productIds: payload.productIds };
      ['personalisation', 'promoCode', 'netPrice'].forEach((key) => {
        if (typeof payload[key] !== 'undefined') {
          data[key] = payload[key];
        }
      });

      const url = `/billing/v1/users/${api.authData.userId}/payments/online`;
      const res = await api.request('post', url, { data });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 204: {
          return undefined;
        }
        case 403: {
          throw new BillingError(res.body.errorType, res.body.payment);
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Initialise payment with Adyen.
     * Auth token is required.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string[]} payload.productIds
     * @param {number=} [payload.netPrice] - Net price in cents.
     *   This value should be the same as the net price in the response from
     *   `subscription.price()` call.
     * @param {string=} [payload.promoCode]
     * @param {string} [payload.returnUrl] url to redirect to from the Adyen page
     *
     * @return {Promise}
     * @fulfill {string} URL of Adyen page to redirect user to.
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {BillingError}
     * @reject {UnknownApiError}
     */
    async adyen(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredStringArray(payload.productIds, 'payload.productIds');
      assertNumber(payload.netPrice, 'payload.netPrice');
      assertString(payload.promoCode, 'payload.promoCode');
      assertString(payload.returnUrl, 'payload.returnUrl');

      const data = { productIds: payload.productIds };
      ['promoCode', 'netPrice', 'returnUrl'].forEach((key) => {
        if (typeof payload[key] !== 'undefined') {
          data[key] = payload[key];
        }
      });

      const url = `/billing/v1/users/${api.authData.userId}/adyen/init`;
      const res = await api.request('post', url, { data });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          throw new BillingError(res.body.message);
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Get encryption key for Adyen payment.
     * Auth token is required.
     *
     * @method
     *
     * @return {Promise}
     * @fulfill {AdyenPublicEncryptionKeyResponse}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError}
     */
    async adyenPublicEncryptionKey() {
      assertAuthData(api.authData);

      const url = `/billing/v1/users/${api.authData.userId}/adyen/encryption-key`;
      const res = await api.request('get', url);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Credit Card payment with Adyen.
     * Auth token is required.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string} payload.encryptedCardData
     * @param {string[]} payload.productIds
     * @param {string=} payload.promoCode
     * @param {number=} payload.netPrice - Net price in cents.
     *   This value should be the same as the net price in the response from
     *   `subscription.price()` call.
     * @param {number=} payload.monthlyNetPrice
     * @param {string=} payload.prepayPeriod
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {BillingError}
     * @reject {PaymentRejectedError}
     * @reject {UnknownApiError}
     */
    async adyenCreditCardPayment(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredString(payload.encryptedCardData, 'payload.encryptedCardData');
      assertStringArray(payload.productIds, 'payload.productIds');
      assertString(payload.promoCode, 'payload.promoCode');
      assertNumber(payload.netPrice, 'payload.netPrice');
      assertNumber(payload.monthlyNetPrice, 'payload.monthlyNetPrice');
      assertString(payload.prepayPeriod, 'payload.prepayPeriod');

      const data = {
        encryptedCardData: payload.encryptedCardData,
        productIds: payload.productIds,
        promoCode: payload.promoCode,
        netPrice: payload.netPrice,
        monthlyNetPrice: payload.monthlyNetPrice,
        prepayPeriod: payload.prepayPeriod,
      };

      const url = `/billing/v1/users/${api.authData.userId}/adyen/credit-card-payment`;
      const res = await api.request('post', url, { data });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          throw new BillingError(res.body.message);
        }
        case 402: {
          throw new PaymentRejectedError('Payment rejected');
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Credit Card registration with Adyen.
     * Auth token is required.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string} payload.encryptedCardData
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {BillingError}
     * @reject {PaymentRejectedError}
     * @reject {UnknownApiError}
     */
    async adyenCreditCardRegistration(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredString(payload.encryptedCardData, 'payload.encryptedCardData');

      const data = {
        encryptedCardData: payload.encryptedCardData,
      };

      const url = `/billing/v1/users/${api.authData.userId}/adyen/credit-card-registration`;
      const res = await api.request('post', url, { data });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          throw new BillingError(res.body.msg);
        }
        case 402: {
          throw new PaymentRejectedError('Payment rejected');
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },
  };

  return billing;
}
